import React, { useState, useEffect } from "react";
import { Modal, Backdrop, Fade, Box } from "@mui/material";
import { IoWarningOutline } from "react-icons/io5";
import { AiFillDelete } from "react-icons/ai";

/**
 * Şifre + şifre onayı (ve isteğe bağlı 2FA) ile silme/onay işlemi için modal.
 * open, onClose, onConfirm({ password, passwordConfirm, code? }), title, confirmButtonText, isLoading, twoFactorEnabled?
 */
const PasswordConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title = "Silme İşlemi",
  subtitle = "Bu işlem geri alınamaz. Lütfen şifrenizi girerek onaylayın.",
  confirmButtonText = "Sil",
  isLoading = false,
  twoFactorEnabled = false,
}) => {
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setPassword("");
      setPasswordConfirm("");
      setCode("");
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const next = {};
    if (!password.trim()) next.password = "Şifreyi girin.";
    if (!passwordConfirm.trim()) next.passwordConfirm = "Şifre onayını girin.";
    if (password !== passwordConfirm && passwordConfirm) next.passwordConfirm = "Şifreler aynı değil.";
    if (twoFactorEnabled && code.trim().length !== 6) next.code = "İki adımlı doğrulama kodu 6 haneli olmalıdır.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onConfirm({
      password: password.trim(),
      passwordConfirm: passwordConfirm.trim(),
      ...(twoFactorEnabled && code.trim() ? { code: code.trim() } : {}),
    });
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 420,
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: 24,
    p: 0,
    outline: "none",
  };

  return (
    <Modal
      open={open}
      onClose={isLoading ? undefined : onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          style: {
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(5px)",
          },
        },
      }}
      style={{ zIndex: 10002 }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="bg-red-50 p-5 flex items-center gap-3 border-b border-red-100">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <IoWarningOutline className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-600">{title}</h3>
                <p className="text-red-500 text-sm mt-0.5">{subtitle}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Şifre</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, password: "" }));
                  }}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                  placeholder="Şifrenizi girin"
                  autoComplete="current-password"
                />
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Şifre Onay</label>
                <input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => {
                    setPasswordConfirm(e.target.value);
                    setErrors((prev) => ({ ...prev, passwordConfirm: "" }));
                  }}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                  placeholder="Şifrenizi tekrar girin"
                  autoComplete="current-password"
                />
                {errors.passwordConfirm && <p className="text-red-500 text-xs mt-1">{errors.passwordConfirm}</p>}
              </div>

              {twoFactorEnabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İki adımlı doğrulama kodu (6 hane)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                      setErrors((prev) => ({ ...prev, code: "" }));
                    }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none text-center tracking-widest"
                    placeholder="000000"
                  />
                  {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 flex justify-center items-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50"
                >
                  {isLoading ? (
                    <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <AiFillDelete className="w-5 h-5" />
                      {confirmButtonText}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
};

export default PasswordConfirmModal;
